import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { ArrowUpRight, Sparkles } from "lucide-react";

// Import your renamed about background image from src/Assets/
import customBgImg from "@/Assets/SCT09350.jpg.jpeg";

// --- Sub-component: Animated Number Counter ---
const AnimatedNumber = ({ value }) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [displayValue, setDisplayValue] = useState(0);

  // Extract number vs text (e.g., "1200+" -> num: 1200, suffix: "+")
  const numericMatch = value.match(/\d+/);
  const targetNum = numericMatch ? parseInt(numericMatch[0], 10) : 0;
  const suffix = value.replace(/\d+/, "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && targetNum > 0) {
      let start = 0;
      const duration = 2000;
      const steps = 60;
      const increment = Math.ceil(targetNum / steps);
      const stepTime = duration / steps;

      const timer = setInterval(() => {
        start += increment;
        if (start >= targetNum) {
          setDisplayValue(targetNum);
          clearInterval(timer);
        } else {
          setDisplayValue(start);
        }
      }, stepTime);

      return () => clearInterval(timer);
    }
  }, [isInView, targetNum]);

  return (
    <span ref={ref}>
      {targetNum > 0 ? displayValue : value}
      {suffix}
    </span>
  );
};

const achievements = [
  { n: "1200+", l: "Weddings Covered" },
  { n: "100+", l: "Editorial Campaigns" },
  { n: "5", l: "Countries" },
  { n: "16y", l: "Crafting Stories" },
];

export default function About() {
  const [visionVisible, setVisionVisible] = useState(false);
  const [milestonesVisible, setMilestonesVisible] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);

  // 3D Tilt State for Founder Vision Card
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  const visionRef = useRef(null);
  const milestonesRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    const observerOptions = { threshold: 0.2 };

    const visionObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisionVisible(true);
    }, observerOptions);

    const milestonesObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setMilestonesVisible(true);
    }, observerOptions);

    const bannerObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setBannerVisible(true);
    }, observerOptions);

    if (visionRef.current) visionObserver.observe(visionRef.current);
    if (milestonesRef.current) milestonesObserver.observe(milestonesRef.current);
    if (bannerRef.current) bannerObserver.observe(bannerRef.current);

    return () => {
      visionObserver.disconnect();
      milestonesObserver.disconnect();
      bannerObserver.disconnect();
    };
  }, []);

  // 3D Tilt Mouse Event Handler
  const handleMouseMove = (e) => {
    if (!visionRef.current) return;
    const rect = visionRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;

    setRotateX(-y * 0.025);
    setRotateY(x * 0.025);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };

  return (
    <MarketingLayout>
      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-28 container-x overflow-hidden" data-testid="about-hero">
        {/* Subtle Ambient Grid Background */}
        <div className="absolute inset-0 bg-[radial-gradient(#283828_1px,transparent_1px)] [background-size:32px_32px] opacity-10 pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">
          <div className="lg:col-span-5">
            <div className="overline text-primary/80 mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.25em]">
              <span className="w-8 h-px bg-primary/80" /> ABOUT DOBHAI OFFICIAL
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              Dobhai.<br />
              <span className="font-serif font-normal text-primary/90">Crafting Tales Of Love.</span>
            </h1>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-muted-foreground leading-relaxed text-lg font-light flex flex-col justify-center">
            <p>
DOBHAI is a creative content and visual storytelling team based in Pakistan and Qatar, with a presence internationally. Founded by Usman Ali, whose photography journey began in 2010, DOBHAI has grown into a trusted creative name.            </p>
            <p className="mt-6 border-l border-primary/30 pl-6 italic text-foreground/80 font-serif">
Our focus is customer satisfaction, creativity, and meaningful storytelling. We bring passion and individuality to every project, turning moments into timeless visual stories.            </p>
          </div>
        </div>
      </section>

      {/* 2. Featured Atmosphere Image with Website's Olive-Green Theme Overlay */}
      <section className="relative w-full h-[650px] md:h-[800px] flex items-center justify-center overflow-hidden group">
        <img
          src={customBgImg}
          alt="Dobhai Studio Atmosphere"
          className="absolute inset-0 w-full h-full object-cover filter contrast-[1.05] scale-100 group-hover:scale-110 transition-transform duration-[2000ms] ease-out"
        />
        {/* Deep Olive Backdrop Blend */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#141F14] via-[#141F14]/80 to-[#141F14]/70 backdrop-blur-[1px]" />

        <div className="relative z-10 container-x max-w-4xl mx-auto text-center px-6 text-[#F1F2EC]">
          <div className="text-[10px] uppercase font-mono tracking-[0.35em] text-[#C9D1B5] mb-6 flex items-center justify-center gap-3">
            <span className="w-8 h-[1px] bg-[#C9D1B5]/60" /> OUR PHILOSOPHY <span className="w-8 h-[1px] bg-[#C9D1B5]/60" />
          </div>
          <blockquote className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[54px] leading-[1.2] font-normal tracking-tight text-white italic mb-8 drop-shadow-xl">
            "The most meaningful images are not forced. They are noticed, felt, and preserved with honesty."
          </blockquote>
          <p className="text-xs sm:text-sm md:text-base text-[#C9D1B5]/90 max-w-2xl mx-auto font-light leading-relaxed tracking-wide drop-shadow-md">
            This is why our process begins long before the wedding day. We listen closely. We learn your priorities, your family dynamics, your pace, your taste, and the emotional shape of the celebration itself. So when you look back, the work does not just feel beautiful. It feels true.
          </p>
        </div>
      </section>

      {/* 3. Vision & Milestones Section with 3D Tilt Card & Live Counters */}
      <section className="py-24 md:py-36 container-x bg-[#FAF9F5] relative" data-testid="mission-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {/* Animated 3D Founder Card */}
          <div 
            ref={visionRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
              transition: "transform 0.15s ease-out, opacity 1s ease-out, scale 1s ease-out",
            }}
            className={`lg:col-span-7 bg-[#EBF0E5] text-[#283828] p-10 md:p-14 relative overflow-hidden rounded-[2px] border border-[#D5E0CD] shadow-xl hover:shadow-2xl transition-all duration-500 ${
              visionVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-16 scale-98"
            }`}
          >
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-primary/40 via-primary to-primary/40" />

            <div className="text-[10px] uppercase tracking-[0.35em] text-primary mb-6 font-mono font-semibold flex items-center gap-2">
              <Sparkles size={12} className="text-primary" /> THE FOUNDER'S VISION
            </div>
            
            <h2 className="font-serif text-3xl md:text-4xl leading-snug mb-6 text-foreground font-normal">
              "Bridging worlds through intentional art."
            </h2>
            
            <p className="text-foreground/85 font-serif text-lg md:text-[20px] leading-relaxed font-light italic">
              "I am Usman Sajid. Operating between Pakistan and Qatar, my vision for Dobhai Official has always been to bridge worlds—bringing the deep cultural richness of subcontinental celebrations together with the refined, sophisticated elegance of international destination storytelling."
            </p>
            
            <div className="mt-10 pt-6 border-t border-primary/20 flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">
              <span>Usman Sajid</span>
              <span>Founder & Lead Director</span>
            </div>
          </div>

          {/* Milestones Column with Auto Counters */}
          <div 
            ref={milestonesRef}
            className={`lg:col-span-5 lg:pl-6 transition-all duration-1000 delay-300 ease-out ${
              milestonesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
            }`}
          >
            <div className="overline text-primary/70 mb-4 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em]">
              <span className="w-6 h-[1px] bg-primary" /> MILESTONES
            </div>
            <h3 className="font-serif text-3xl md:text-4xl mb-8 text-foreground tracking-tight">
              A legacy of trusted excellence.
            </h3>
            
            <div className="grid grid-cols-2 gap-10">
              {achievements.map((a, idx) => (
                <div 
                  key={a.l} 
                  style={{ transitionDelay: `${400 + idx * 100}ms` }}
                  className={`border-l-2 border-primary/80 pl-5 transition-all duration-700 hover:translate-x-1.5 ${
                    milestonesVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                  }`}
                >
                  <div className="font-serif text-4xl md:text-5xl text-foreground font-normal tracking-tight">
                    <AnimatedNumber value={a.n} />
                  </div>
                  <div className="overline text-muted-foreground mt-2 text-[10px] tracking-[0.25em] font-mono uppercase">
                    {a.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 4. Luxury Dark Editorial CTA Banner */}
      <section ref={bannerRef} className="relative py-32 md:py-44 overflow-hidden bg-[#162016] text-[#F1F2EC]" data-testid="about-cta-banner">
        <div className="absolute inset-0 pointer-events-none select-none">
          <img
            src={customBgImg}
            alt="Dobhai Official Celebrations"
            className="w-full h-full object-cover opacity-20 filter grayscale scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141F14]/95 via-[#162016]/90 to-[#121A12]/98" />
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#2E422E]/30 rounded-full blur-3xl" />
        </div>

        <div className="container-x relative text-center z-10 max-w-4xl mx-auto px-6">
          <div className={`text-[10px] uppercase font-mono tracking-[0.35em] text-[#C9D1B5]/85 mb-6 transition-all duration-1000 ease-out ${bannerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            WE'D LOVE TO HEAR FROM YOU
          </div>

          <h2 className={`font-serif text-4xl sm:text-5xl md:text-6xl lg:text-[68px] font-normal leading-[1.08] tracking-tight transition-all duration-1000 delay-200 ease-out ${bannerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            Let's create something<br />
            <span className=" font-serif text-[#C9D1B5]">beautiful, lasting, and unmistakably yours.</span>
          </h2>

          <p className={`mt-6 md:mt-8 text-xs sm:text-sm md:text-base text-[#A8B29C] max-w-xl mx-auto font-light leading-relaxed transition-all duration-1000 delay-400 ease-out ${bannerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            If you are planning a celebration in Pakistan or Qatar, whether you are based locally or returning from abroad, we would love to hear your story and guide you toward the right experience.
          </p>

          <div className={`mt-12 flex justify-center transition-all duration-1000 delay-600 ease-out ${bannerVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-12 scale-95"}`}>
            <Link
              to="/booking"
              className="group px-10 py-4 border border-[#C9D1B5]/60 bg-transparent text-[#F1F2EC] font-semibold text-[11px] tracking-[0.25em] uppercase hover:bg-[#C9D1B5] hover:text-[#1A241A] transition-all duration-500 shadow-xl inline-flex items-center gap-3"
            >
              <span>Begin Your Enquiry</span>
              <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}